/**
 * Modal de Crear/Editar Campo Personalizado.
 *
 * Este componente proporciona una interfaz modal para crear y editar
 * campos personalizados en los formularios de registro de eventos,
 * permitiendo definir el nombre, tipo, opciones y si es requerido.
 *
 * @author Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 * @date [Fecha de creación: 2025-11-12]
 * @updated [Fecha de última modificación: 2025-11-12]
 * @updatedBy Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 */

import React from "react";
import {
  Modal,
  Card,
  Icon,
  Divider,
  TextField,
  Grid,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

/**
 * Componente modal para crear o editar campos personalizados.
 *
 * Este componente renderiza un modal con un formulario que permite crear
 * nuevos campos personalizados o editar los existentes, con validación
 * de tipos y opciones según el tipo de campo seleccionado.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {boolean} props.open - Controla si el modal está abierto.
 * @param {Function} props.onClose - Función que se ejecuta al cerrar el modal.
 * @param {Object|null} props.editingField - Campo en edición o null si es nuevo.
 * @param {Object} props.fieldForm - Objeto con los datos del formulario del campo.
 * @param {string} props.fieldForm.label - Nombre/etiqueta del campo.
 * @param {string} props.fieldForm.type - Tipo del campo (alphabetic, numeric, etc).
 * @param {boolean} props.fieldForm.required - Si el campo es obligatorio.
 * @param {Array|string} props.fieldForm.options - Opciones para campos tipo select.
 * @param {Function} props.onFieldChange - Función para actualizar valores del formulario.
 * @param {Function} props.onSave - Función que se ejecuta al guardar el campo.
 * @param {boolean} props.saving - Indica si se está guardando actualmente.
 * @returns {JSX.Element} Modal con formulario de campo personalizado.
 */
export default function CustomFieldModal({
  open,
  onClose,
  editingField,
  fieldForm,
  onFieldChange,
  onSave,
  saving,
}) {
  /**
   * Maneja el cambio del switch de campo requerido.
   *
   * Este método captura el evento del switch y actualiza el estado
   * del campo required con el valor booleano correspondiente.
   *
   * @param {Event} event - Evento del switch.
   * @returns {void} No retorna valor, ejecuta onFieldChange.
   */
  const handleSwitchChange = (event) => {
    onFieldChange("required", event.target.checked);
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "600px",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-field-title"
      aria-describedby="modal-field-description"
    >
      <Card sx={style}>
        <MDBox
          mt={-7}
          color="white"
          bgColor="info"
          variant="gradient"
          borderRadius="lg"
          p={2}
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <MDTypography variant="h5" color="white">
            {editingField ? "Editar Campo Personalizado" : "Crear Campo Personalizado"}
          </MDTypography>
          <Icon
            sx={{
              fontSize: "2em",
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
              color: "white",
            }}
            onClick={onClose}
          >
            close
          </Icon>
        </MDBox>

        <Divider sx={{ my: 0 }} />

        <MDBox pt={4} pb={3} px={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del Campo"
                value={fieldForm.label}
                onChange={(e) => onFieldChange("label", e.target.value)}
                required
                variant="outlined"
                size="medium"
                InputLabelProps={{
                  sx: { fontSize: "1rem" },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Tipo de Campo"
                value={fieldForm.type}
                onChange={(e) => onFieldChange("type", e.target.value)}
                required
                variant="outlined"
                size="medium"
                InputLabelProps={{
                  sx: { fontSize: "1rem" },
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  },
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: "42px",
                  },
                  "& .MuiSelect-select": {
                    minHeight: "42px !important",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                <MenuItem value="alphabetic">Alfabético</MenuItem>
                <MenuItem value="numeric">Numérico</MenuItem>
                <MenuItem value="alphanumeric">Alfanumérico</MenuItem>
                <MenuItem value="select">Lista de Opciones</MenuItem>
                <MenuItem value="checklist">Checklist (Selección Múltiple)</MenuItem>
              </TextField>
            </Grid>

            {fieldForm.type === "select" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Opciones (separadas por coma)"
                  value={
                    typeof fieldForm.options === "string"
                      ? fieldForm.options
                      : Array.isArray(fieldForm.options)
                      ? fieldForm.options.join(", ")
                      : ""
                  }
                  onChange={(e) => onFieldChange("options", e.target.value)}
                  required
                  helperText="Ejemplo: Opción 1, Opción 2, Opción 3"
                  multiline
                  rows={3}
                  variant="outlined"
                  size="medium"
                  InputLabelProps={{
                    sx: { fontSize: "1rem" },
                  }}
                />
              </Grid>
            )}

            {fieldForm.type === "checklist" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Opciones del Checklist (separadas por coma)"
                  value={
                    typeof fieldForm.options === "string"
                      ? fieldForm.options
                      : Array.isArray(fieldForm.options)
                      ? fieldForm.options.join(", ")
                      : ""
                  }
                  onChange={(e) => onFieldChange("options", e.target.value)}
                  required
                  helperText="El usuario podrá seleccionar varias opciones. Ejemplo: Opción A, Opción B, Opción C"
                  multiline
                  rows={3}
                  variant="outlined"
                  size="medium"
                  InputLabelProps={{
                    sx: { fontSize: "1rem" },
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <MDBox display="flex" alignItems="center" gap={2}>
                <Switch
                  checked={Boolean(fieldForm.required)}
                  onChange={handleSwitchChange}
                  color="info"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#49a3f1",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#49a3f1",
                    },
                  }}
                />
                <Typography sx={{ fontSize: "1rem", fontWeight: 500 }}>Campo requerido</Typography>
              </MDBox>
            </Grid>
          </Grid>

          <MDBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
            <MDButton onClick={onClose} color="error" disabled={saving}>
              Cancelar
            </MDButton>
            <MDButton onClick={onSave} color="success" disabled={saving}>
              {saving ? "Guardando..." : editingField ? "Actualizar" : "Crear"}
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </Modal>
  );
}
